import Vue from 'vue';
import types from '../../mutation-types';
import getters from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import {
  CONVERSATION_LIST_KEYS,
  findConversationByDisplayId,
  getSelectedChatConversation,
  ensureConversationInList,
  upsertConversationInListSortedByLatest,
  removeConversationFromList,
  mergeConversationAttributes,
  reconcileAssigneeListMembership,
  listKeysForNewConversation,
} from './helpers/conversationLists';

const emptyConversationLists = () => ({
  me: [],
  unassigned: [],
  all: [],
  appliedFilters: [],
});

const state = {
  conversationLists: emptyConversationLists(),
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.ALL,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  /** Loading flags for contact panel fetches / new conversation from contact */
  contactSidebarUiFlags: {
    isFetching: false,
    isCreating: false,
    /** Set when fetchContactConversations fails; cleared on next successful fetch start */
    fetchError: null,
  },
};

function chatByDisplayId(s, id) {
  return findConversationByDisplayId(s, id);
}

function replaceConversationEverywhere(_state, merged) {
  CONVERSATION_LIST_KEYS.forEach(key => {
    const list = _state.conversationLists[key];
    const idx = list.findIndex(c => Number(c.id) === Number(merged.id));
    if (idx > -1) Vue.set(list, idx, merged);
  });
}

function normalizeUpdatePayload(payload) {
  if (payload && payload.conversation) {
    return {
      conversation: payload.conversation,
      currentUserId: payload.currentUserId,
    };
  }
  return { conversation: payload, currentUserId: undefined };
}

// mutations
export const mutations = {
  [types.SET_CONTACT_SIDEBAR_UI_FLAG](_state, data) {
    _state.contactSidebarUiFlags = {
      ..._state.contactSidebarUiFlags,
      ...data,
    };
  },
  [types.SET_ALL_CONVERSATION](_state, { listKey, conversationList }) {
    if (!listKey || !Array.isArray(conversationList)) return;
    const target = _state.conversationLists[listKey];
    if (!target) return;

    conversationList.forEach(conversation => {
      const existing = findConversationByDisplayId(_state, conversation.id);
      if (existing) {
        const merged = mergeConversationAttributes(existing, conversation);
        replaceConversationEverywhere(_state, merged);
        upsertConversationInListSortedByLatest(target, merged);
      } else {
        upsertConversationInListSortedByLatest(target, conversation);
      }
    });
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.conversationLists = emptyConversationLists();
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;
    Vue.set(chat, 'allMessagesLoaded', true);
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;
    Vue.set(chat, 'allMessagesLoaded', false);
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data || !data.length) return;
    const chat = chatByDisplayId(_state, id);
    if (!chat) return;
    if (!chat.messages) Vue.set(chat, 'messages', []);
    chat.messages.unshift(...data);
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const chat = chatByDisplayId(_state, id);
    if (!chat) return;
    Vue.set(chat, 'attachments', []);
    chat.attachments.push(...data);
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const chat = chatByDisplayId(_state, id);
    if (!chat) return;
    Vue.set(chat, 'messages', data);
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, payload) {
    const assignee =
      payload && typeof payload === 'object' && 'assignee' in payload
        ? payload.assignee
        : payload;
    const currentUserId =
      payload && typeof payload === 'object' && 'currentUserId' in payload
        ? payload.currentUserId
        : undefined;
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;
    Vue.set(chat.meta, 'assignee', assignee);
    if (currentUserId !== undefined) {
      reconcileAssigneeListMembership(_state, chat, currentUserId);
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const chat = chatByDisplayId(_state, conversationId);
    if (!chat) return;
    Vue.set(chat.meta, 'team', team);
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const chat = chatByDisplayId(_state, conversationId);
    if (chat) {
      Vue.set(chat, 'last_activity_at', lastActivityAt);
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const chat = chatByDisplayId(_state, conversationId);
    if (!chat) return;
    Vue.set(chat, 'priority', priority);
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;
    Vue.set(chat, 'custom_attributes', custom_attributes);
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    Vue.set(conversation, 'snoozed_until', snoozedUntil);
    Vue.set(conversation, 'status', status);
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;
    Vue.set(chat, 'muted', true);
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return;
    Vue.set(chat, 'muted', false);
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    const { conversation_id: conversationId } = message;
    const chat = chatByDisplayId(_state, conversationId);

    if (!chat) return;

    const isMessageSent =
      message.status === MESSAGE_STATUS.SENT && message.attachments;
    if (isMessageSent) {
      message.attachments.forEach(attachment => {
        if (!chat.attachments.some(a => a.id === attachment.id)) {
          chat.attachments.push(attachment);
        }
      });
    }
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    const { conversation_id: conversationId } = message;
    const chat = chatByDisplayId(_state, conversationId);

    if (!chat) return;

    const isMessageSent = message.status === MESSAGE_STATUS.SENT;
    if (isMessageSent) {
      const attachmentIndex = chat.attachments.findIndex(
        a => a.message_id === message.id
      );
      if (attachmentIndex !== -1) chat.attachments.splice(attachmentIndex, 1);
    }
  },

  [types.ADD_MESSAGE](_state, message) {
    const { conversation_id: conversationId } = message;
    const { selectedChatId } = _state;
    const chat = chatByDisplayId(_state, conversationId);
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      Vue.set(chat.messages, pendingMessageIndex, message);
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
      if (Number(selectedChatId) === Number(conversationId)) {
        window.bus.$emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        window.bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.ADD_CONVERSATION](_state, { conversation, listKeys, currentUserId }) {
    const existing = findConversationByDisplayId(_state, conversation.id);
    if (existing) {
      const merged = mergeConversationAttributes(existing, conversation);
      replaceConversationEverywhere(_state, merged);
      listKeys.forEach(key => {
        upsertConversationInListSortedByLatest(
          _state.conversationLists[key],
          merged
        );
      });
      reconcileAssigneeListMembership(_state, merged, currentUserId);
      return;
    }
    listKeys.forEach(key => {
      upsertConversationInListSortedByLatest(
        _state.conversationLists[key],
        conversation
      );
    });
    reconcileAssigneeListMembership(_state, conversation, currentUserId);
  },

  [types.UPDATE_CONVERSATION](_state, payload) {
    const { conversation, currentUserId } = normalizeUpdatePayload(payload);
    const existing = findConversationByDisplayId(_state, conversation.id);
    if (existing) {
      const merged = mergeConversationAttributes(existing, conversation);
      replaceConversationEverywhere(_state, merged);
      reconcileAssigneeListMembership(_state, merged, currentUserId);
      if (Number(_state.selectedChatId) === Number(conversation.id)) {
        window.bus.$emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        window.bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      const keys = listKeysForNewConversation(
        conversation,
        currentUserId
      );
      keys.forEach(key => {
        upsertConversationInListSortedByLatest(
          _state.conversationLists[key],
          conversation
        );
      });
      reconcileAssigneeListMembership(_state, conversation, currentUserId);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const chat = chatByDisplayId(_state, id);
    if (chat) {
      Vue.set(chat, 'agent_last_seen_at', lastSeen);
      Vue.set(chat, 'unread_count', unreadCount);
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.UPDATE_ASSIGNEE](_state, payload) {
    const { id, assignee, currentUserId } = payload;
    const chat = chatByDisplayId(_state, id);
    if (!chat) return;
    Vue.set(chat.meta, 'assignee', assignee);
    reconcileAssigneeListMembership(_state, chat, currentUserId);
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const chat = chatByDisplayId(_state, conversationId);
    if (chat) {
      Vue.set(chat.meta, 'sender', payload);
    }
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const chat = chatByDisplayId(_state, conversationId);
    if (chat) {
      Vue.set(chat, 'can_reply', canReply);
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    CONVERSATION_LIST_KEYS.forEach(key => {
      const list = _state.conversationLists[key];
      const next = list.filter(c => c.meta?.sender?.id !== contactId);
      Vue.set(_state.conversationLists, key, next);
    });
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.UPDATE_CONVERSATION_BOT_STATUS](_state, { conversationId, botDisabled }) {
    const chat = chatByDisplayId(_state, conversationId);
    if (chat) {
      if (!chat.additional_attributes) {
        Vue.set(chat, 'additional_attributes', {});
      }
      Vue.set(chat.additional_attributes, 'bot_disabled', botDisabled);
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
